import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

import BaseLink from "~/components/links/BaseLink";
import MainSubNav from "~/components/SubNav/MainSubNav";
import SubNavLinks from "~/components/SubNav/SubNavLinks";
import MainMobileSubNav from "~/components/SubNav/MainMobileSubNav";
import { SingleSubNavRight } from "~/components/Header/TopHeader";

import transition from "~/utilities/transition";

import { breakpointNumbers } from "~/theme";

import {
    KEYCODE_SPACE,
    KEYCODE_TAB,
    KEYCODE_ESCAPE,
} from "~/utilities/helpers";

import {
    keyboardMenuNavigation,
    checkNavigationTarget,
    navCloseFocusManagement,
    initializeAccessibility,
    mobileNavCloseFocusManagement,
    mobileMenuFocusManagement,
    mobileMenuKeyboardNavigation,
    mobileSecondaryNavKeyboardNavigation,
} from "~/utilities/accessibilityHelpers";

import { useMultiRef } from "~/hooks/useMultiRef";
import useOnKeyPress from "~/hooks/useOnKeyPress";

const NavItem = styled.div`
    display: flex;
    align-items: center;
    &:not(:first-of-type) {
        margin-left: 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        &:not(:first-of-type) {
            margin-left: ${({ theme }) => theme.layout.gutter};
        }
    }
`;

const MainNavLinkWrapper = styled.div`
    position: ${({ singleColumnSubNavigation }) =>
        singleColumnSubNavigation ? `relative` : `static`};
    margin-bottom: ${({ theme }) => theme.layout.gutter};

    &:not(:first-of-type) {
        margin-left: 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        margin-bottom: inherit;

        &:not(:first-of-type) {
            margin-left: ${({ theme }) => theme.layout.gutter};
        }
    }
`;

const MainNavLink = styled(BaseLink)`
    ${transition({}, "border-bottom")};
    letter-spacing: 0.056em;
    position: relative;
    padding-bottom: 37px;
    color: ${({ theme }) => theme.palette.white};
    text-transform: uppercase;
    font-weight: 500;
    font-size: 1.8rem;

    &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        border-bottom: ${({ theme, isOpen }) =>
            isOpen ? `7px solid ${theme.palette.lightBlue}` : `none`};
    }
`;

const MobileMainNavLink = styled.button`
    text-transform: none;
    font-weight: 900;
    font-size: 1.9rem;
    letter-spacing: 0.056em;
    color: ${({ theme }) => theme.palette.darkBlue};
    text-align: left;
`;

const MainNav = ({
    cards = [],
    handleDispatch,
    iconLinks = [],
    index,
    linkLabel,
    links = [],
    linkUrl,
    mobileMenuIsOpen,
    mobileSubNavIsOpen,
    numberOfColumns = 4,
    openMainSubnav,
    singleColumnSubNavigation = false,
    type,
}) => {
    const hasSubNav = type !== "noSubNavigation";

    const [accessibilityInitialized, setAccessibilityInitialized] =
        useState(false);
    const [closingSubnav, setClosingSubnav] = useState();

    const navRef = useMultiRef();
    const mobileNavRef = useMultiRef();

    useOnKeyPress(KEYCODE_TAB, (keyboardEvent) => {
        if (mobileMenuIsOpen) {
            if (breakpointNumbers.mobileLandscape >= window.innerWidth) {
                mobileSecondaryNavKeyboardNavigation({
                    keyboardEvent,
                    mobileSubNavIsOpen,
                });
            } else {
                mobileMenuKeyboardNavigation({ keyboardEvent, mobileNavRef });
            }
        } else {
            keyboardMenuNavigation({ keyboardEvent, navRef });
        }
    });

    useOnKeyPress(KEYCODE_SPACE, (keyboardEvent) => {
        if (!mobileMenuIsOpen) {
            const validTarget = checkNavigationTarget({
                keyboardEvent,
                navRef,
            });
            if (validTarget) {
                keyboardEvent.preventDefault();
                if (openMainSubnav === `${linkLabel}-${index}`) {
                    keyboardEvent.target.click();
                } else {
                    handleDispatch(
                        "HANDLE_MOUSE_ENTER",
                        `${linkLabel}-${index}`,
                        hasSubNav,
                    );
                }
            }
        } else if (mobileMenuIsOpen && mobileSubNavIsOpen.includes(linkLabel)) {
            setClosingSubnav(mobileSubNavIsOpen);
        }
    });

    useOnKeyPress(KEYCODE_ESCAPE, (keyboardEvent) => {
        if (mobileMenuIsOpen) {
            mobileNavCloseFocusManagement();
        } else {
            navCloseFocusManagement({
                keyboardEvent,
            });
        }
        handleDispatch("HANDLE_CLOSE_ALL", null, hasSubNav);
    });

    useEffect(() => {
        if (closingSubnav === `${linkLabel}-${index}` && mobileMenuIsOpen) {
            mobileMenuFocusManagement({
                closingSubnav,
                mobileNavRef,
                setClosingSubnav,
            });
        } else if (mobileSubNavIsOpen === `${linkLabel}-${index}`) {
            mobileMenuFocusManagement({ mobileSubNavIsOpen, mobileNavRef });
        }
    }, [
        mobileMenuIsOpen,
        mobileSubNavIsOpen,
        closingSubnav,
        mobileNavRef,
        linkLabel,
        index,
    ]);

    if (!accessibilityInitialized) {
        initializeAccessibility({
            navRef,
            setAccessibilityInitialized,
        });
    }

    return (
        <NavItem>
            {!mobileSubNavIsOpen ? (
                <MainNavLinkWrapper
                    {...{ singleColumnSubNavigation }}
                    onMouseEnter={() =>
                        handleDispatch(
                            "HANDLE_MOUSE_ENTER",
                            `${linkLabel}-${index}`,
                            hasSubNav,
                        )
                    }
                    onMouseLeave={() =>
                        handleDispatch("HANDLE_MOUSE_LEAVE", null, hasSubNav)
                    }
                    onClick={() =>
                        handleDispatch(
                            "HANDLE_MOBILE_SUB_NAV",
                            `${linkLabel}-${index}`,
                            hasSubNav,
                        )
                    }
                >
                    {!mobileMenuIsOpen ? (
                        <MainNavLink
                            ariaExpanded={
                                openMainSubnav === `${linkLabel}-${index}`
                            }
                            ariaLabel={`${linkLabel} submenu`}
                            tagRef={navRef.parent}
                            to={linkUrl}
                            tagLabel={`${linkLabel}-Top`}
                            isOpen={openMainSubnav === `${linkLabel}-${index}`}
                        >
                            {linkLabel}
                        </MainNavLink>
                    ) : (
                        <MobileMainNavLink
                            type="button"
                            data-label={`${linkLabel}-Mobile-Top`}
                            aria-label={`${linkLabel} submenu`}
                        >
                            {linkLabel}
                        </MobileMainNavLink>
                    )}
                    {hasSubNav && !singleColumnSubNavigation ? (
                        <MainSubNav
                            {...{
                                type,
                                links,
                                cards,
                                iconLinks,
                                numberOfColumns,
                            }}
                            tagLabel={linkLabel}
                            isOpen={openMainSubnav === `${linkLabel}-${index}`}
                        />
                    ) : hasSubNav &&
                      singleColumnSubNavigation &&
                      type === "links" ? (
                        <SingleSubNavRight
                            {...{ singleColumnSubNavigation }}
                            isOpen={openMainSubnav === `${linkLabel}-${index}`}
                        >
                            <SubNavLinks
                                {...{ links }}
                                numberOfColumns={1}
                                tagLabel={linkLabel}
                            />
                        </SingleSubNavRight>
                    ) : null}
                </MainNavLinkWrapper>
            ) : null}
            {hasSubNav ? (
                <MainMobileSubNav
                    isOpen={mobileSubNavIsOpen === `${linkLabel}-${index}`}
                    tagRef={mobileNavRef}
                    {...{ linkLabel, linkUrl, type, links, cards, iconLinks }}
                />
            ) : null}
        </NavItem>
    );
};

MainNav.propTypes = {
    linkLabel: PropTypes.string.isRequired,
    linkUrl: PropTypes.string.isRequired,
    singleColumnSubNavigation: PropTypes.bool,
    numberOfColumns: PropTypes.number,
    type: PropTypes.oneOf(["links", "cards", "iconLinks", "noSubNavigation"])
        .isRequired,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            subNavigation: PropTypes.arrayOf(
                PropTypes.shape({
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            logo: PropTypes.shape({
                sourceUrl: PropTypes.string,
                altText: PropTypes.string,
                title: PropTypes.string.isRequired,
            }),
            logoBackgroundColor: PropTypes.string,
            title: PropTypes.string,
            subTitle: PropTypes.string,
            productsImage: PropTypes.shape({
                sourceUrl: PropTypes.string,
                altText: PropTypes.string,
                title: PropTypes.string.isRequired,
            }),
            buttonLabel: PropTypes.string,
            buttonUrl: PropTypes.string,
            additionalLinks: PropTypes.arrayOf(
                PropTypes.shape({
                    linkLabel: PropTypes.string.isRequired,
                    linkUrl: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    iconLinks: PropTypes.arrayOf(
        PropTypes.shape({
            iconName: PropTypes.string,
            linkUrl: PropTypes.string.isRequired,
            linkLabel: PropTypes.string.isRequired,
        }),
    ),
    mobileMenuIsOpen: PropTypes.bool.isRequired,
    mobileSubNavIsOpen: PropTypes.string.isRequired,
    openMainSubnav: PropTypes.string.isRequired,
    handleDispatch: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
};

export default MainNav;
